"use client";

import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useStore } from "../services/store";
import { useClusterText } from "./useClusterText";

const MarketOverviewSection = observer(function MarketOverviewSection({
  basePath = "/cluster",
}) {
  const store = useStore();
  const { text, err, loading } = useClusterText({
    basePath,
    formattedDate: store.formattedDate,
  });
  const {
    text: pushText,
    loading: pushLoading,
  } = useClusterText({
    basePath: `${basePath}/push`,
    formattedDate: store.formattedDate,
  });
  const [expanded, setExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);
  const contentRef = useRef(null);

  const CLAMP_HEIGHT = 180;
  const combinedText = [pushText, text]
    .map((value) => (value || "").trim())
    .filter(Boolean)
    .join("\n\n");

  useEffect(() => {
    if (!contentRef.current) return;
    const el = contentRef.current;
    setCanExpand(el.scrollHeight > CLAMP_HEIGHT + 8);
  }, [combinedText, loading, pushLoading, err]);

  if (!combinedText && !loading && !pushLoading) return null;

  const content =
    loading || pushLoading
      ? combinedText || "Loading market overview..."
      : combinedText;
  const showError =
    !loading &&
    !pushLoading &&
    err &&
    (!text || text.trim().length === 0) &&
    (!pushText || pushText.trim().length === 0);

  return (
    <div style={{ marginTop: 12, marginBottom: 18 }}>
      <h2 className="f4 fw6 mb2 light-yellow" style={{ color: "#FFD166" }}>
        Market Overview
      </h2>
      <div
        ref={contentRef}
        style={{
          backgroundColor: "#0f1115",
          border: "1px solid #242833",
          borderRadius: 10,
          padding: "12px 14px",
          color: "#e5e7eb",
          lineHeight: 1.55,
          fontSize: "0.95rem",
          whiteSpace: "pre-wrap",
          maxHeight: expanded ? "none" : CLAMP_HEIGHT,
          overflow: expanded ? "visible" : "hidden",
          position: "relative",
        }}
      >
        {showError ? "No market overview yet." : content}
        {!expanded && canExpand && (
          <div
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: 24,
              background:
                "linear-gradient(to bottom, rgba(0,0,0,0), rgba(0,0,0,.65))",
              pointerEvents: "none",
            }}
          />
        )}
      </div>
      {canExpand && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 6,
            marginTop: 4,
            paddingTop: 6,
            borderTop: "1px solid rgba(255,255,255,0.08)",
            background:
              "linear-gradient(to bottom, rgba(255,255,255,0.02), rgba(0,0,0,0))",
          }}
        >
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-label={expanded ? "Collapse overview" : "Expand overview"}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: 2,
              color: "#9ca3af",
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 12,
              letterSpacing: "0.02em",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.color = "#e5e7eb";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.color = "#9ca3af";
            }}
          >
            <span>{expanded ? "less" : "more"}</span>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              style={{
                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 150ms ease",
              }}
            >
              <path
                d="M6 9l6 6 6-6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
});

export default MarketOverviewSection;
